import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { attorneys } from "../../data/attorneys";
import styles from "./AttorneyDetail.module.css";
import { FaArrowLeft, FaEnvelope, FaMapMarkerAlt, FaBriefcase, FaGraduationCap, FaGavel, FaCheckCircle, FaUsers, FaBuilding, FaBook, FaMicrophone, FaPhone } from "react-icons/fa";

const AttorneyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const attorney = attorneys.find((a) => a.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!attorney) {
        return (
            <div className={styles.errorContainer}>
                <h1>Attorney Not Found</h1>
                <button onClick={() => navigate("/")} className={styles.backButton}>
                    <FaArrowLeft /> Back to Team
                </button>
            </div>
        );
    }

    const { name, role, image, details } = attorney;

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.header}>
                <button onClick={() => navigate("/")} className={styles.backButton}>
                    <FaArrowLeft /> BACK TO TEAM
                </button>
            </header>

            <main className={styles.container}>
                <div className={styles.profileSection}>
                    <div className={styles.imageContainer}>
                        <img src={image} alt={name} className={styles.profileImage} />
                    </div>
                    <div className={styles.profileInfo}>
                        <h1>{name}</h1>
                        <p className={styles.role}>{role}</p>

                        <div className={styles.quickContact}>
                            {details.phone && (
                                <p>
                                    <FaPhone className={styles.icon} /> {details.phone}
                                </p>
                            )}
                            {details.email && (
                                <p>
                                    <FaEnvelope className={styles.icon} /> {details.email}
                                </p>
                            )}
                            {details.location && (
                                <p>
                                    <FaMapMarkerAlt className={styles.icon} /> {details.location}
                                </p>
                            )}
                        </div>

                        <div className={styles.firmInfo}>
                            <p><strong>{details.title || role}</strong></p>
                            <p>{details.firm || "Liberty & Justice Advocates"}</p>
                        </div>
                    </div>
                </div>

                <div className={styles.contentGrid}>
                    <div className={styles.mainContent}>
                        {/* About / Bio Section */}
                        {(details.about || details.bio) && (
                            <section className={styles.section}>
                                <h2><FaBriefcase className={styles.sectionIcon} /> About</h2>
                                {details.about ? (
                                    details.about.map((para, i) => <p key={i}>{para}</p>)
                                ) : (
                                    <p>{details.bio}</p>
                                )}
                            </section>
                        )}

                        {/* Representative Transactions Section */}
                        {details.representativeTransactions && (
                            <section className={styles.section}>
                                <h2><FaCheckCircle className={styles.sectionIcon} /> Representative Transactions</h2>
                                <ul className={styles.list}>
                                    {details.representativeTransactions.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Speaking Engagements Section */}
                        {details.speakingEngagements && (
                            <section className={styles.section}>
                                <h2><FaMicrophone className={styles.sectionIcon} /> Speaking Engagements</h2>
                                <ul className={styles.list}>
                                    {details.speakingEngagements.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>

                    <aside className={styles.sidebar}>
                        {/* Bars & Courts */}
                        {details.barsAndCourts && (
                            <div className={styles.sidebarBox}>
                                <h3><FaGavel className={styles.sidebarIcon} /> Bars & Courts</h3>
                                <ul>
                                    {details.barsAndCourts.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* CPA */}
                        {details.cpa && (
                            <div className={styles.sidebarBox}>
                                <h3><FaBuilding className={styles.sidebarIcon} /> Certified Public Accountant</h3>
                                <ul>
                                    {details.cpa.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Education */}
                        {(details.education) && (
                            <div className={styles.sidebarBox}>
                                <h3><FaGraduationCap className={styles.sidebarIcon} /> Education</h3>
                                <ul>
                                    {details.education ? (
                                        details.education.map((item, i) => <li key={i}>{item}</li>)
                                    ) : (
                                        <li>Details available upon request</li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Practice Areas */}
                        {details.practiceAreas && (
                            <div className={styles.sidebarBox}>
                                <h3><FaCheckCircle className={styles.sidebarIcon} /> Practice Areas</h3>
                                <ul>
                                    {details.practiceAreas.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Professional Affiliations */}
                        {details.professionalAffiliations && (
                            <div className={styles.sidebarBox}>
                                <h3><FaUsers className={styles.sidebarIcon} /> Professional Affiliations</h3>
                                <ul>
                                    {details.professionalAffiliations.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Community Affiliations */}
                        {details.communityAffiliations && (
                            <div className={styles.sidebarBox}>
                                <h3><FaUsers className={styles.sidebarIcon} /> Community Affiliations</h3>
                                <ul>
                                    {details.communityAffiliations.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Publications */}
                        {details.publications && (
                            <div className={styles.sidebarBox}>
                                <h3><FaBook className={styles.sidebarIcon} /> Publications</h3>
                                <ul>
                                    {details.publications.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Services (Fallback for other attorneys) */}
                        {details.services && !details.practiceAreas && (
                            <div className={styles.sidebarBox}>
                                <h3>Services</h3>
                                <ul>
                                    {details.services.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}
                    </aside>
                </div>
            </main>

            <footer className={styles.footer}>
                <p>© {new Date().getFullYear()} Liberty & Justice Advocates. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default AttorneyDetail;
